import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './CDLoanPayment.css';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';
import { maskAadhaar, maskPAN } from '../../utils/sensitiveDataUtils';

const CDLoanPayment = () => {
  const { id, type } = useParams();
  const searchType = type || 'name';
  const [loanDetails, setLoanDetails] = useState(null);
  const [dueDateInfo, setDueDateInfo] = useState(null);
  const [overdueInterestDetails, setOverdueInterestDetails] = useState([]);
  const [showOverdueInterest, setShowOverdueInterest] = useState(false);
  const [overdueInterestRate, setOverdueInterestRate] = useState(15); // Default 15% per month
  const [currentDueDate, setCurrentDueDate] = useState('');
  const [interestPaymentMode, setInterestPaymentMode] = useState('');
  const [interestTransactionId, setInterestTransactionId] = useState('');
  const [loanPaymentMode, setLoanPaymentMode] = useState('');
  const [loanTransactionId, setLoanTransactionId] = useState('');
  const [payInterest, setPayInterest] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [isRenewal, setIsRenewal] = useState(false);
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false); // Track successful payment
  const [paymentStatus, setPaymentStatus] = useState(''); // Track payment status (Unpaid, Partially Paid, Paid)
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]); // Payment date for interest calculation
  const [commissionInterestRate, setCommissionInterestRate] = useState(36); // Default 36% per annum (3% per month)
  const [showCommissionInterest, setShowCommissionInterest] = useState(false);
  const [calculatedInterest, setCalculatedInterest] = useState(0); // Calculated interest based on payment date
  const [calculatedCommissionInterest, setCalculatedCommissionInterest] = useState(0); // Calculated commission interest
  const [interestPaidDate, setInterestPaidDate] = useState(null); // Track when interest was last paid (to check renewal)
  const [showRenewalDialog, setShowRenewalDialog] = useState(false); // Show renewal confirmation dialog
  const [interestPaidWithoutRenewal, setInterestPaidWithoutRenewal] = useState(false); // Track if interest was paid without renewal
  const [lastInterestPaid, setLastInterestPaid] = useState(0); // Store last interest paid for slip generation
  const [lastAmountPaid, setLastAmountPaid] = useState(0); // Store last amount paid for slip generation
  const [partialPaymentAmount, setPartialPaymentAmount] = useState(''); // Partial payment amount for renewal
  const [showPartialPayment, setShowPartialPayment] = useState(false); // Show partial payment input when renewing
  const [lastInterestPaymentDate, setLastInterestPaymentDate] = useState(null); // Last interest payment date (renewal date)
  const [interestTransactions, setInterestTransactions] = useState([]); // Interest payment transactions
  const [partialPaymentTransactions, setPartialPaymentTransactions] = useState([]); // Partial payment transactions
  const [overduePeriods, setOverduePeriods] = useState([]); // Overdue periods with details
  const [partialOverdueAmount, setPartialOverdueAmount] = useState({}); // Partial overdue payment amounts by period
  const [renewalDate, setRenewalDate] = useState(null); // Renewal date (payment date + 30 days)
  const [isOverdue, setIsOverdue] = useState(false); // Whether payment date exceeds renewal date
  const [daysOverdue, setDaysOverdue] = useState(0); // Days overdue

  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/cd-loan-reciever`, {
          params: { type: searchType, value: id },
        });
        setLoanDetails(response.data);
      } catch (error) {
        console.error('Error fetching loan details:', error);
      }
    };

    fetchLoanDetails();
  }, [id, searchType]);

  useEffect(() => {
    if (loanDetails && loanDetails.account_no) {
      const fetchDueDateDetails = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/cd-emi-details/${loanDetails.account_no}`);
          // CD loans have only one due date (maturity date)
          if (response.data && response.data.length > 0) {
            const dueDateData = response.data[0]; // Get the single due date
            setCurrentDueDate(dueDateData.due_date);
            setPaymentStatus(dueDateData.status || 'Unpaid');
            setDueDateInfo(dueDateData); // Store for display
            
            // Fetch interest payment transactions and get last payment date
            try {
              const interestPaymentRes = await axios.get(`${API_BASE_URL}/cd-commission-interest/${loanDetails.account_no}`);
              if (interestPaymentRes.data && interestPaymentRes.data.length > 0) {
                setInterestTransactions(interestPaymentRes.data);
                const lastPayment = interestPaymentRes.data[0]; // Already sorted DESC by payment_date
                const lastPaymentDate = lastPayment.payment_date || lastPayment.due_date;
                setInterestPaidDate(lastPaymentDate);
                setLastInterestPaymentDate(lastPaymentDate); // Set as renewal date for interest calculation
                
                // Check if loan was renewed by comparing due date
                const loanStartDate = new Date(loanDetails.date);
                const currentDueDateObj = new Date(dueDateData.due_date);
                const daysFromStart = Math.floor((currentDueDateObj.getTime() - loanStartDate.getTime()) / (1000 * 60 * 60 * 24));
                
                // If due date is still around 30 days from start, loan was not renewed
                if (daysFromStart <= 30) {
                  setInterestPaidWithoutRenewal(true);
                }
              } else {
                // No interest paid yet, use loan start date
                setLastInterestPaymentDate(loanDetails.date);
              }
            } catch (err) {
              console.log('Could not fetch interest payment date');
              setLastInterestPaymentDate(loanDetails.date); // Fallback to loan start date
            }
            
            // Fetch partial payment transactions (principal payments)
            try {
              const partialPaymentsRes = await axios.get(`${API_BASE_URL}/cd-partial-payments/${loanDetails.account_no}`);
              if (partialPaymentsRes.data) {
                setPartialPaymentTransactions(partialPaymentsRes.data);
              }
            } catch (err) {
              console.log('Could not fetch partial payment transactions');
            }
          } else {
            // Fallback: get due date directly from loan if endpoint fails
            const [dueDateResponse] = await axios.get(`${API_BASE_URL}/cd-due-date/${loanDetails.account_no}`).catch(() => [null]);
            if (dueDateResponse && dueDateResponse.data) {
              setCurrentDueDate(dueDateResponse.data.due_date);
            }
          }
        } catch (error) {
          console.error('Error fetching due date details:', error);
          // Try to get due date from loan details if available
          if (loanDetails.due_date) {
            setCurrentDueDate(loanDetails.due_date);
          }
        }
      };

      fetchDueDateDetails();
    }
  }, [loanDetails]);

  // Calculate interest based on payment date (days from last renewal date, not loan start date)
  // Interest should not be calculated beyond 30 days
  // Interest is calculated on Outstanding Balance (Original Loan - Principal Paid)
  useEffect(() => {
    if (loanDetails && loanDetails.loan_amount && paymentDate) {
      // Use last interest payment date (renewal date) if available, otherwise use loan start date
      const baseDate = lastInterestPaymentDate ? new Date(lastInterestPaymentDate) : new Date(loanDetails.date);
      const paymentDateObj = new Date(paymentDate);
      
      // Set time to start of day for accurate calculation
      baseDate.setHours(0, 0, 0, 0);
      paymentDateObj.setHours(0, 0, 0, 0);
      
      // Calculate days from last renewal date (or loan start date) to payment date
      const timeDiff = paymentDateObj.getTime() - baseDate.getTime();
      let daysFromRenewal = Math.max(0, Math.floor(timeDiff / (1000 * 60 * 60 * 24)));
      
      // Cap interest calculation at 30 days maximum
      const daysForInterest = Math.min(daysFromRenewal, 30);
      
      // Calculate Outstanding Balance: Original Loan - Principal Paid
      const originalLoanAmount = parseFloat(loanDetails.loan_amount || 0);
      const principalPaid = parseFloat(dueDateInfo?.principal_paid || 0);
      const outstandingBalance = Math.max(0, originalLoanAmount - principalPaid);
      
      // Calculate interest on Outstanding Balance: (Outstanding Balance * 16 * Days) / (365 * 100) - capped at 30 days
      const interest = (outstandingBalance * 16 * daysForInterest) / (365 * 100);
      setCalculatedInterest(interest);
      setPayInterest(interest.toFixed(2));
      
      // Calculate commission interest on Outstanding Balance: (Outstanding Balance * Rate * Days) / (360 * 100) - capped at 30 days
      const commissionInterest = (outstandingBalance * commissionInterestRate * daysForInterest) / (360 * 100);
      setCalculatedCommissionInterest(commissionInterest);
    }
  }, [loanDetails, paymentDate, commissionInterestRate, lastInterestPaymentDate, dueDateInfo]);

  // Calculate renewal date and check for overdue
  // Renewal date is payment date + 30 days (this is the new renewal date after payment)
  // But we check overdue by comparing payment date to expected renewal date (last renewal + 30 days)
  useEffect(() => {
    if (paymentDate) {
      const paymentDateObj = new Date(paymentDate);
      paymentDateObj.setHours(0, 0, 0, 0);
      
      // Calculate renewal date: payment date + 30 days (this will be the new renewal date after payment)
      const renewalDateObj = new Date(paymentDateObj);
      renewalDateObj.setDate(renewalDateObj.getDate() + 30);
      setRenewalDate(renewalDateObj);
      
      // Check if payment date exceeds expected renewal date
      // Expected renewal date is last interest payment date + 30 days (or loan start date + 30 days if no payment yet)
      const baseDate = lastInterestPaymentDate ? new Date(lastInterestPaymentDate) : (loanDetails && loanDetails.date ? new Date(loanDetails.date) : null);
      
      if (baseDate) {
        baseDate.setHours(0, 0, 0, 0);
        
        // Expected renewal date is base date + 30 days
        const expectedRenewalDate = new Date(baseDate);
        expectedRenewalDate.setDate(expectedRenewalDate.getDate() + 30);
        
        // Check if payment date is after expected renewal date
        if (paymentDateObj > expectedRenewalDate) {
          const daysOverdueCalc = Math.floor((paymentDateObj.getTime() - expectedRenewalDate.getTime()) / (1000 * 60 * 60 * 24));
          setIsOverdue(true);
          setDaysOverdue(daysOverdueCalc);
        } else {
          setIsOverdue(false);
          setDaysOverdue(0);
        }
      } else {
        setIsOverdue(false);
        setDaysOverdue(0);
      }
    }
  }, [paymentDate, lastInterestPaymentDate, loanDetails]);

  // Map API overdue periods → calculated OD rows (keeps prior unpaid OD after renewal)
  const mapOverduePeriods = (periods) => {
    return (periods || [])
      .filter(period => parseFloat(period.overdueDays || 0) > 0)
      .map(period => {
        const monthsOverdue = period.overdueDays / 30;
        const overdueInterest = (overdueInterestRate / 100) * monthsOverdue * period.loanAmount;
        const overdueRemaining = overdueInterest - (parseFloat(period.overduePaid) || 0);
        return {
          ...period,
          overdueInterest: overdueInterest.toFixed(2),
          overdueRemaining: overdueRemaining > 0 ? overdueRemaining.toFixed(2) : '0.00'
        };
      });
  };

  // Fetch overdue periods (prior unpaid OD + current open OD)
  useEffect(() => {
    if (loanDetails && loanDetails.account_no) {
      const fetchOverduePeriods = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/cd-overdue-periods/${loanDetails.account_no}`, {
            params: paymentDate ? { asOf: paymentDate } : undefined
          });
          console.log('Overdue periods response:', response.data);
          if (response.data && response.data.length > 0) {
            const periodsWithOverdue = mapOverduePeriods(response.data);
            console.log('Periods with overdue calculated:', periodsWithOverdue);
            setOverduePeriods(periodsWithOverdue);
            setOverdueInterestDetails(periodsWithOverdue.filter(p => parseFloat(p.overdueRemaining) > 0));
          } else {
            setOverduePeriods([]);
            setOverdueInterestDetails([]);
          }
        } catch (error) {
          console.error('Error fetching overdue periods:', error);
          setOverduePeriods([]);
          setOverdueInterestDetails([]);
        }
      };
      
      fetchOverduePeriods();
    }
  }, [loanDetails, overdueInterestRate, paymentDate]);
 

  // Handle overdue payment submission (supports partial payment)
  const handleOverduePayment = async (period, isPartial = false) => {
    try {
      const overdueAmount = isPartial ? parseFloat(partialOverdueAmount[period.periodNumber] || 0) : parseFloat(period.overdueRemaining || period.overdueInterest || 0);
      
      if (overdueAmount <= 0) {
        alert('Please enter a valid overdue amount.');
        return;
      }
      
      if (isPartial && overdueAmount > parseFloat(period.overdueRemaining || period.overdueInterest || 0)) {
        alert(`Partial payment cannot exceed remaining overdue amount ₹${parseFloat(period.overdueRemaining || period.overdueInterest || 0).toFixed(2)}`);
        return;
      }
      
      await axios.post(`${API_BASE_URL}/cd-overdue-payment`, {
        account_no: loanDetails.account_no,
        debtor_name: loanDetails.loanee_name,
        overdue_paid: overdueAmount,
        installment_date: period.dueDate,
      });
      
      alert(`Overdue Payment of ₹${overdueAmount.toFixed(2)} Successful!`);
      
      // Refresh overdue periods (includes prior unpaid OD + current)
      const response = await axios.get(`${API_BASE_URL}/cd-overdue-periods/${loanDetails.account_no}`, {
        params: paymentDate ? { asOf: paymentDate } : undefined
      });
      if (response.data && response.data.length > 0) {
        const periodsWithOverdue = mapOverduePeriods(response.data);
        setOverduePeriods(periodsWithOverdue);
        setOverdueInterestDetails(periodsWithOverdue.filter(p => parseFloat(p.overdueRemaining) > 0));
      } else {
        setOverduePeriods([]);
        setOverdueInterestDetails([]);
      }
      
      // Clear partial payment amount for this period
      setPartialOverdueAmount({ ...partialOverdueAmount, [period.periodNumber]: '' });
    } catch (error) {
      console.error('Error processing overdue payment:', error);
      if (error.response && error.response.data && error.response.data.error) {
        alert(error.response.data.error);
      } else {
        alert('Overdue Payment failed. Please try again.');
      }
    }
  };

  // Handle interest payment button click - show confirmation dialog
  const handleInterestPaymentClick = () => {
    if (!currentDueDate || !interestPaymentMode) {
      alert('Please choose a payment mode for interest payment.');
      return;
    }
    
    const interestPaid = parseFloat(payInterest) || 0;
    
    if (interestPaid <= 0) {
      alert('Please enter the interest amount to pay.');
      return;
    }

    // Show dialog to confirm renewal choice
    setShowRenewalDialog(true);
  };

  // Handle interest payment submission (after confirmation)
  const handleInterestPayment = async (shouldRenew) => {
    setShowRenewalDialog(false);
    
    const interestPaid = parseFloat(payInterest) || 0;
    const partialAmount = shouldRenew && showPartialPayment ? parseFloat(partialPaymentAmount) || 0 : 0;

    // Validate partial payment if provided
    if (shouldRenew && showPartialPayment && partialAmount > 0) {
      const loanAmount = parseFloat(loanDetails.loan_amount || 0);
      const principalPaid = parseFloat(dueDateInfo?.principal_paid || 0);
      const remainingLoan = loanAmount - principalPaid;
      
      if (partialAmount > remainingLoan) {
        alert(`Partial payment cannot exceed remaining loan amount ₹${remainingLoan.toFixed(2)}`);
        setShowRenewalDialog(true);
        return;
      }
    }

    try {
      await axios.post(`${API_BASE_URL}/cd-pay-emi`, {
        account_no: loanDetails.account_no,
        due_date: currentDueDate,
        payment_mode: interestPaymentMode,
        transaction_id: interestTransactionId || null,
        amount: partialAmount, // Partial payment amount if provided
        interest_paid: interestPaid,
        is_renewal: shouldRenew,
        commission_interest_rate: commissionInterestRate, // Send commission interest rate
        payment_date: paymentDate, // Send payment date for accurate calculation
        partial_payment: partialAmount > 0 ? partialAmount : null // Explicit partial payment flag
      });
      
      let message = 'Interest Payment Successful!';
      if (shouldRenew) {
        message += ' Loan has been renewed for another 30 days.';
        if (partialAmount > 0) {
          message += ` ₹${partialAmount.toFixed(2)} has been deducted from the loan amount.`;
        }
      } else {
        message += ' Payment completed without renewal.';
        // If payment is made without renewal, disable interest payment
        setInterestPaidWithoutRenewal(true);
      }
      alert(message);
      
      // Store payment amount before clearing
      setLastInterestPaid(interestPaid);
      
      setIsPaymentSuccessful(true);
      setInterestPaymentMode('');
      setInterestTransactionId('');
      setIsRenewal(shouldRenew);
      setInterestPaidDate(paymentDate); // Update interest paid date
      setLastInterestPaymentDate(paymentDate); // Update last interest payment date for next calculation
      setPartialPaymentAmount(''); // Clear partial payment
      setShowPartialPayment(false); // Reset partial payment flag
      
      // Refresh data
      const emiRes = await axios.get(`${API_BASE_URL}/cd-emi-details/${loanDetails.account_no}`);
      if (emiRes.data && emiRes.data.length > 0) {
        const dueDateData = emiRes.data[0];
        setDueDateInfo(dueDateData);
        setCurrentDueDate(dueDateData.due_date);
        setPaymentStatus(dueDateData.status || 'Unpaid');
      }
      
      // Refresh loan details to get updated loan amount
      const loanRes = await axios.get(`${API_BASE_URL}/cd-loan-reciever`, {
        params: { type: searchType, value: id },
      });
      if (loanRes.data) {
        setLoanDetails(loanRes.data);
      }
      
      // Refresh interest transactions
      try {
        const interestPaymentRes = await axios.get(`${API_BASE_URL}/cd-commission-interest/${loanDetails.account_no}`);
        if (interestPaymentRes.data && interestPaymentRes.data.length > 0) {
          setInterestTransactions(interestPaymentRes.data);
        }
      } catch (err) {
        console.log('Could not refresh interest transactions');
      }
      
      // Refresh partial payment transactions
      try {
        const partialPaymentsRes = await axios.get(`${API_BASE_URL}/cd-partial-payments/${loanDetails.account_no}`);
        if (partialPaymentsRes.data) {
          setPartialPaymentTransactions(partialPaymentsRes.data);
        }
      } catch (err) {
        console.log('Could not refresh partial payment transactions');
      }
      
      // Refresh overdue periods (includes prior unpaid OD after renewal)
      try {
        const overdueRes = await axios.get(`${API_BASE_URL}/cd-overdue-periods/${loanDetails.account_no}`, {
          params: paymentDate ? { asOf: paymentDate } : undefined
        });
        if (overdueRes.data && overdueRes.data.length > 0) {
          const periodsWithOverdue = mapOverduePeriods(overdueRes.data);
          setOverduePeriods(periodsWithOverdue);
          setOverdueInterestDetails(periodsWithOverdue.filter(p => parseFloat(p.overdueRemaining) > 0));
        } else {
          setOverduePeriods([]);
          setOverdueInterestDetails([]);
        }
      } catch (err) {
        console.log('Could not refresh overdue periods');
      }
      
      // Generate interest slip automatically after successful payment
      setTimeout(() => {
        generateInterestSlip();
      }, 500);
      
      // Recalculate interest for next payment
      // The useEffect will handle this automatically when paymentDate changes
    } catch (error) {
      console.error('Error processing interest payment:', error);
      if (error.response && error.response.data && error.response.data.error) {
        alert(error.response.data.error);
      } else {
        alert('Interest payment failed. Please try again.');
      }
    }
  };

  // Handle loan amount (principal) payment submission
  const handleLoanAmountPayment = async () => {
    if (!currentDueDate || !loanPaymentMode) {
      alert('Please choose a payment mode for loan amount payment.');
      return;
    }
    
    const amountPaid = parseFloat(payAmount) || 0;
    
    if (amountPaid <= 0) {
      alert('Please enter the loan amount to pay.');
      return;
    }

    const loanAmount = parseFloat(loanDetails.loan_amount || 0);
    if (amountPaid > loanAmount) {
      alert(`Payment cannot exceed loan amount ₹${loanAmount.toFixed(2)}`);
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/cd-pay-emi`, {
        account_no: loanDetails.account_no,
        due_date: currentDueDate,
        payment_mode: loanPaymentMode,
        transaction_id: loanTransactionId || null,
        amount: amountPaid,
        interest_paid: 0, // No interest payment
        is_renewal: false // Renewal is only for interest payment
      });
      
      alert('Loan Amount Payment Successful!');
      
      // Store payment amount before clearing
      setLastAmountPaid(amountPaid);
      
      setIsPaymentSuccessful(true);
      setPayAmount('');
      setLoanPaymentMode('');
      setLoanTransactionId('');
      
      // Refresh data
      const emiRes = await axios.get(`${API_BASE_URL}/cd-emi-details/${loanDetails.account_no}`);
      if (emiRes.data && emiRes.data.length > 0) {
        const dueDateData = emiRes.data[0];
        setDueDateInfo(dueDateData);
        setCurrentDueDate(dueDateData.due_date);
        setPaymentStatus(dueDateData.status || 'Unpaid');
      }
      
      // Refresh partial payment transactions
      try {
        const partialPaymentsRes = await axios.get(`${API_BASE_URL}/cd-partial-payments/${loanDetails.account_no}`);
        if (partialPaymentsRes.data) {
          setPartialPaymentTransactions(partialPaymentsRes.data);
        }
      } catch (err) {
        console.log('Could not refresh partial payment transactions');
      }
      
      // Generate total amount slip automatically after successful payment
      setTimeout(() => {
        generateTotalAmountSlip();
      }, 500);
    } catch (error) {
      console.error('Error processing loan amount payment:', error);
      if (error.response && error.response.data && error.response.data.error) {
        alert(error.response.data.error);
      } else {
        alert('Loan amount payment failed. Please try again.');
      }
    }
  };

  // Generate and print interest slip (similar to GL interest slip)
  const generateInterestSlip = (transactionAmount = null) => {
    const interestPaid = transactionAmount !== null ? transactionAmount : (parseFloat(payInterest) || lastInterestPaid || 0);

    const slipContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 5px; }
  .slip-title { text-align: center; font-weight: bold; font-size: 1.1em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="slip-title">Interest Slip</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date(paymentDate))}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Loan Amount Rs:</strong> ₹${parseFloat(loanDetails.loan_amount || 0).toFixed(2)}</div>
      <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
    </div>
    <table class="table">
      <tr>
        <td>CDInterest A/c</td>
        <td class="right">${interestPaid.toFixed(2)}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹${interestPaid.toFixed(2)}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="slip-title">Interest Slip</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date(paymentDate))}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Loan Amount Rs:</strong> ₹${parseFloat(loanDetails.loan_amount || 0).toFixed(2)}</div>
      <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
    </div>
    <table class="table">
      <tr>
        <td>CDInterest A/c</td>
        <td class="right">${interestPaid.toFixed(2)}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹${interestPaid.toFixed(2)}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;
    const printWindow = window.open('', '', 'height=800,width=1200');
    printWindow.document.write('<html><head><title>Interest Slip</title></head><body>');
    printWindow.document.write(slipContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  // Generate and print total amount slip (for loan amount payment)
  const generateTotalAmountSlip = () => {
    const amountPaid = parseFloat(payAmount) || lastAmountPaid || 0;

    const slipContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Loan Amount Rs:</strong> ₹${parseFloat(loanDetails.loan_amount || 0).toFixed(2)}</div>
      <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
    </div>
    <table class="table">
      <tr>
        <td>Loan Amount A/c</td>
        <td class="right">₹${amountPaid.toFixed(2)}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹${amountPaid.toFixed(2)}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Loan Amount Rs:</strong> ₹${parseFloat(loanDetails.loan_amount || 0).toFixed(2)}</div>
      <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
    </div>
    <table class="table">
      <tr>
        <td>Loan Amount A/c</td>
        <td class="right">₹${amountPaid.toFixed(2)}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹${amountPaid.toFixed(2)}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;
    const printWindow = window.open('', '', 'height=800,width=1200');
    printWindow.document.write('<html><head><title>Total Amount Slip</title></head><body>');
    printWindow.document.write(slipContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  // Toggle overdue interest table on Ctrl + B key press
  // Toggle commission interest table on Ctrl + N key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Ctrl+B (or Cmd+B on Mac) - Show overdue interest
      if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault(); // Prevent browser bookmark shortcut
        setShowOverdueInterest((prev) => {
          const newValue = !prev;
          console.log('Ctrl+B pressed - Showing overdue:', newValue);
          return newValue;
        });
      }
      // Check for Ctrl+N (or Cmd+N on Mac) - Show commission interest
      if ((e.ctrlKey || e.metaKey) && (e.key === 'e' || e.key === 'E')) {
        e.preventDefault(); // Prevent browser new window shortcut
        setShowCommissionInterest((prev) => {
          const newValue = !prev;
          console.log('Ctrl+E pressed - Showing commission interest:', newValue);
          return newValue;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!loanDetails) {
    return <h2>Loading loan details...</h2>;
  }

  return (
    <div className="loan-payment-container">
      <div className="loan-payment-header">Loan Payment</div>

      {/* Loan Details */}
      <div className="loan-details">
        <p><strong>Account No:</strong> {loanDetails.account_no}</p>
        <p><strong>Loanee Name:</strong> {loanDetails.loanee_name}</p>
        <p><strong>Date:</strong> {formatDateToDDMMYYYY(loanDetails.date)}</p>
        <p><strong>Aadhaar Debtor:</strong> {maskAadhaar(loanDetails.aadhaar_debtor)}</p>
        <p><strong>PAN Card:</strong> {maskPAN(loanDetails.pan_card)}</p>
        <p><strong>Address Debtor:</strong> {loanDetails.address_debtor}</p>
        <p><strong>Guarantor Name:</strong> {loanDetails.guarantor_name}</p>
        <p><strong>Loan Amount:</strong> ₹{loanDetails.loan_amount}</p>
        <p><strong>Period:</strong> {loanDetails.period} days</p>
      </div>

      {/* Loan Summary */}
      <div className="loan-summary" style={{ 
        marginTop: '20px', 
        padding: '20px', 
        border: '2px solid #007bff', 
        borderRadius: '8px', 
        backgroundColor: '#f0f8ff',
        marginBottom: '20px'
      }}>
        <h3 style={{ marginTop: 0, color: '#007bff' }}>Loan Summary</h3>
        {loanDetails && (
          <div>
            <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>
              <strong>Outstanding Balance:</strong> ₹{Math.max(0, (parseFloat(loanDetails.loan_amount || 0) - parseFloat(dueDateInfo?.principal_paid || 0))).toFixed(2)}
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
              (Original Loan: ₹{parseFloat(loanDetails.loan_amount || 0).toFixed(2)} - Principal Paid: ₹{parseFloat(dueDateInfo?.principal_paid || 0).toFixed(2)})
            </p>
          </div>
        )}
      </div>

      {/* Due Date Information */}
      <div className="emi-details">
        <h3>Due Date Information</h3>
        {currentDueDate ? (
          <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
            <p><strong>Maturity Date (Due Date):</strong> {formatDateToDDMMYYYY(currentDueDate)}</p>
            <p><strong>Status:</strong> 
              <span style={{ 
                color: paymentStatus === 'Paid' ? 'green' : paymentStatus === 'Partially Paid' ? 'orange' : 'red',
                fontWeight: 'bold',
                marginLeft: '10px'
              }}>
                {paymentStatus || 'Unpaid'}
              </span>
            </p>
          </div>
        ) : (
          <div style={{ padding: '15px', color: '#666' }}>Loading due date information...</div>
        )}
      </div>

      {/* Overdue Interest Details */}
      {showOverdueInterest && (
        <div className="overdue-interest-details" style={{ 
          marginTop: '20px', 
          padding: '20px', 
          border: '2px solid #dc3545', 
          borderRadius: '8px', 
          backgroundColor: '#fff3cd' 
        }}>
          <h3 style={{ marginTop: 0, color: '#dc3545' }}>Overdue Interest Details</h3>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ marginRight: '10px' }}>Overdue Interest Rate (% per month):</label>
            <input
              type="number"
              value={overdueInterestRate}
              onChange={(e) => setOverdueInterestRate(e.target.value)}
              style={{ padding: '5px', width: '100px' }}
            />
          </div>
          {overduePeriods.length > 0 && overduePeriods.some(p => parseFloat(p.overdueDays || 0) > 0) ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#dc3545', color: 'white' }}>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Period</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Start Date</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Due Date</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Balance Amount</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Days Overdue</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Overdue Interest</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Overdue Paid</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Remaining</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Partial Payment</th>
                  <th style={{ padding: '10px', border: '1px solid #ccc' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {overduePeriods.filter(p => parseFloat(p.overdueDays || 0) > 0).map((period, index) => (
                  <tr key={`${period.dueDate}-${period.periodNumber}-${index}`} style={{ backgroundColor: parseFloat(period.overdueRemaining) > 0 ? '#fff3cd' : '#d4edda' }}>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                      {period.periodNumber}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                      {formatDateToDDMMYYYY(period.startDate)}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                      {formatDateToDDMMYYYY(period.dueDate)}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                      ₹{parseFloat(period.loanAmount).toFixed(2)}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                      {period.overdueDays} days
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center', fontWeight: 'bold' }}>
                      ₹{period.overdueInterest}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center', color: 'green' }}>
                      ₹{parseFloat(period.overduePaid || 0).toFixed(2)}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center', fontWeight: 'bold', color: parseFloat(period.overdueRemaining) > 0 ? 'red' : 'green' }}>
                      ₹{period.overdueRemaining}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                      {parseFloat(period.overdueRemaining) > 0 ? (
                        <input
                          type="number"
                          value={partialOverdueAmount[period.periodNumber] || ''}
                          onChange={(e) => setPartialOverdueAmount({ ...partialOverdueAmount, [period.periodNumber]: e.target.value })}
                          placeholder="Amount"
                          step="0.01"
                          min="0"
                          max={parseFloat(period.overdueRemaining)}
                          style={{ width: '100px', padding: '5px' }}
                        />
                      ) : (
                        <span style={{ color: 'green' }}>Paid</span>
                      )}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                      {parseFloat(period.overdueRemaining) > 0 ? (
                        <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                          <button 
                            onClick={() => handleOverduePayment(period, false)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '5px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Pay Full
                          </button>
                          {partialOverdueAmount[period.periodNumber] && parseFloat(partialOverdueAmount[period.periodNumber]) > 0 && (
                            <button 
                              onClick={() => handleOverduePayment(period, true)}
                              style={{
                                padding: '5px 10px',
                                backgroundColor: '#ffc107',
                                color: 'black',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Pay Partial
                            </button>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: 'green', fontWeight: 'bold' }}>✓ Paid</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              backgroundColor: '#d4edda', 
              borderRadius: '5px',
              color: '#155724'
            }}>
              <strong>No Overdue Interest</strong>
              <p style={{ margin: '10px 0 0 0' }}>
                {currentDueDate ? (
                  <>The loan is not overdue. Due date: {formatDateToDDMMYYYY(currentDueDate)}</>
                ) : (
                  'Due date information not available.'
                )}
              </p>
            </div>
          )}
          <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
            <strong>Note:</strong> Press Ctrl+B again to hide this section.
          </div>
        </div>
      )}

      {/* Commission Interest Details */}
      {showCommissionInterest && (
        <div className="commission-interest-details" style={{ 
          marginTop: '20px', 
          padding: '20px', 
          border: '2px solid #6c757d', 
          borderRadius: '8px', 
          backgroundColor: '#f8f9fa' 
        }}>
          <h3 style={{ marginTop: 0, color: '#6c757d' }}>Commission Interest Details</h3>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ marginRight: '10px' }}>Commission Interest Rate (% per month):</label>
            <input
              type="number"
              value={(commissionInterestRate / 12).toFixed(2)}
              onChange={(e) => {
                const monthlyRate = parseFloat(e.target.value) || 3;
                const annualRate = monthlyRate * 12;
                setCommissionInterestRate(annualRate);
              }}
              style={{ padding: '5px', width: '100px' }}
              step="0.01"
            />
            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
              Default: {(commissionInterestRate / 12).toFixed(2)}% per month (equals {commissionInterestRate}% per annum). Modify as needed. The rate will be saved when you make payment.
            </small>
          </div>
          {loanDetails && dueDateInfo && (
            <div style={{ padding: '15px', background: 'white', borderRadius: '5px' }}>
              <p><strong>Original Loan Amount:</strong> ₹{parseFloat(loanDetails.loan_amount || 0).toFixed(2)}</p>
              <p><strong>Principal Paid:</strong> ₹{parseFloat(dueDateInfo?.principal_paid || 0).toFixed(2)}</p>
              <p style={{ marginBottom: '10px' }}>
                <strong>Outstanding Balance:</strong> 
                <span style={{ color: '#007bff', fontWeight: 'bold', marginLeft: '10px' }}>
                  ₹{Math.max(0, (parseFloat(loanDetails.loan_amount || 0) - parseFloat(dueDateInfo?.principal_paid || 0))).toFixed(2)}
                </span>
              </p>
              <p><strong>Payment Date:</strong> {formatDateToDDMMYYYY(paymentDate)}</p>
              <p><strong>Days:</strong> {
                lastInterestPaymentDate ? (
                  Math.min(30, Math.max(0, Math.floor((new Date(paymentDate).getTime() - new Date(lastInterestPaymentDate).getTime()) / (1000 * 60 * 60 * 24))))
                ) : (
                  loanDetails.date ? Math.min(30, Math.max(0, Math.floor((new Date(paymentDate).getTime() - new Date(loanDetails.date).getTime()) / (1000 * 60 * 60 * 24)))) : 0
                )
              } {lastInterestPaymentDate || loanDetails.date ? (
                <>(Interest calculated for {Math.min(30, Math.max(0, Math.floor((new Date(paymentDate).getTime() - (lastInterestPaymentDate ? new Date(lastInterestPaymentDate).getTime() : new Date(loanDetails.date).getTime())) / (1000 * 60 * 60 * 24))))} days maximum)</>
              ) : ''}</p>
              <p style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '15px' }}>
                <strong>Calculated Commission Interest:</strong> ₹{calculatedCommissionInterest.toFixed(2)}
              </p>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                <strong>Formula:</strong> (Outstanding Balance × {commissionInterestRate} × Days) / (360 × 100)
              </p>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                [Per month: {(commissionInterestRate / 12).toFixed(2)}% × 12 = {commissionInterestRate}% per annum]
              </p>
              <p style={{ fontSize: '12px', color: '#28a745', marginTop: '10px', fontWeight: 'bold' }}>
                <strong>Note:</strong> Interest is calculated on the outstanding balance (original loan amount - principal paid)
              </p>
            </div>
          )}
          <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
            <strong>Note:</strong> Press Ctrl+E again to hide this section.
          </div>
        </div>
      )}

      {/* Payment Form */}
      <div className="payment-form" style={{ 
        opacity: (paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) ? 0.5 : 1,
        pointerEvents: (paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) ? 'none' : 'auto'
      }}>
        <h3>Make Payment</h3>
        {(paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) && (
          <div style={{ padding: '10px', background: '#d4edda', borderRadius: '5px', marginBottom: '15px', color: '#155724' }}>
            <strong>Payment Complete:</strong> This loan has been fully paid (principal and interest). Payment is disabled.
          </div>
        )}
        <div>
          <label>Due Date (Renewal Date):</label>
          <input
            type="text"
            value={currentDueDate ? formatDateToDDMMYYYY(currentDueDate) : ''}
            readOnly
            style={{ backgroundColor: '#f0f0f0' }}
          />
        </div>
        <div style={{ marginTop: '15px' }}>
          <label>Payment Date (for interest calculation):</label>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
            Interest will be calculated from last renewal date to this payment date (maximum 30 days)
          </small>
          {renewalDate && (
            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
              Renewal Date: {formatDateToDDMMYYYY(renewalDate)}
            </small>
          )}
          {isOverdue && (
            <div style={{ 
              marginTop: '10px', 
              padding: '10px', 
              backgroundColor: '#f8d7da', 
              borderRadius: '5px', 
              border: '1px solid #dc3545',
              color: '#dc3545',
              fontWeight: 'bold'
            }}>
              ⚠️ OVERDUE: {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue
            </div>
          )}
        </div>
        
        {/* Interest Payment Section */}
        <div style={{ border: '2px solid #007bff', padding: '15px', borderRadius: '8px', marginBottom: '20px', marginTop: '20px', background: '#f0f8ff' }}>
          <h4 style={{ marginTop: 0, color: '#007bff' }}>Pay Interest</h4>
          <div>
            <label>Pay Interest (₹):</label>
            <input
              type="number"
              value={payInterest}
              onChange={(e) => setPayInterest(e.target.value)}
              placeholder="Enter interest amount"
              step="0.01"
              disabled={(paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) || interestPaidWithoutRenewal}
            />
            {loanDetails && dueDateInfo && (
              <>
                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                  Calculated Interest (16% per annum): ₹{calculatedInterest.toFixed(2)} | Days: {
                    lastInterestPaymentDate ? (
                      Math.min(30, Math.max(0, Math.floor((new Date(paymentDate).getTime() - new Date(lastInterestPaymentDate).getTime()) / (1000 * 60 * 60 * 24))))
                    ) : (
                      loanDetails.date ? Math.min(30, Math.max(0, Math.floor((new Date(paymentDate).getTime() - new Date(loanDetails.date).getTime()) / (1000 * 60 * 60 * 24)))) : 0
                    )
                  } {lastInterestPaymentDate || loanDetails.date ? (
                    <>(Interest calculated for {Math.min(30, Math.max(0, Math.floor((new Date(paymentDate).getTime() - (lastInterestPaymentDate ? new Date(lastInterestPaymentDate).getTime() : new Date(loanDetails.date).getTime())) / (1000 * 60 * 60 * 24))))} days maximum)</>
                  ) : ''}
                </small>
                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                  Interest calculated on Outstanding Balance: ₹{Math.max(0, (parseFloat(loanDetails.loan_amount || 0) - parseFloat(dueDateInfo?.principal_paid || 0))).toFixed(2)}
                </small>
                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                  Formula: (Outstanding Balance × 16 × Days) / (365 × 100)
                </small>
              </>
            )}
          </div>
          <div style={{ marginTop: '15px' }}>
            <label>Payment Mode:</label>
            <select
              value={interestPaymentMode}
              onChange={(e) => setInterestPaymentMode(e.target.value)}
              disabled={(paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) || interestPaidWithoutRenewal}
            >
              <option value="">Select Payment Mode</option>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Cheque">Cheque</option>
            </select>
          </div>
          <div style={{ marginTop: '10px' }}>
            <label>Transaction ID:</label>
            <input
              type="text"
              value={interestTransactionId}
              onChange={(e) => setInterestTransactionId(e.target.value)}
              placeholder="Transaction ID (optional)"
              disabled={(paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) || interestPaidWithoutRenewal}
            />
          </div>
          <div style={{ marginTop: '15px' }}>
            <small style={{ color: '#666', display: 'block', marginTop: '5px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '5px' }}>
              <strong>Note:</strong> When you click "Pay Interest", you will be asked whether to renew the loan for another 30 days or proceed without renewal.
            </small>
          </div>
          {interestPaidWithoutRenewal && (
            <div style={{ 
              padding: '10px', 
              background: '#fff3cd', 
              borderRadius: '5px', 
              marginTop: '15px', 
              color: '#856404',
              border: '1px solid #ffc107'
            }}>
              <strong>Note:</strong> Interest has been paid without renewal. Interest payment is now disabled.
            </div>
          )}
          <button 
            onClick={handleInterestPaymentClick}
            disabled={(paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) || interestPaidWithoutRenewal}
            style={{ 
              marginTop: '15px', 
              padding: '10px 20px',
              backgroundColor: ((paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) || interestPaidWithoutRenewal) ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: ((paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) || interestPaidWithoutRenewal) ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              opacity: ((paymentStatus === 'Paid' && dueDateInfo && dueDateInfo.interest_paid > 0) || interestPaidWithoutRenewal) ? 0.6 : 1
            }}
          >
            Pay Interest
          </button>
        </div>

        {/* Loan Amount Payment Section */}
        <div style={{ border: '2px solid #28a745', padding: '15px', borderRadius: '8px', background: '#f0fff4' }}>
          <h4 style={{ marginTop: 0, color: '#28a745' }}>Pay Loan Amount</h4>
          <div>
            <label>Pay Loan Amount (₹):</label>
            <input
              type="number"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              placeholder="Enter loan amount"
              step="0.01"
              disabled={paymentStatus === 'Paid'}
            />
            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
              Total loan amount: ₹{parseFloat(loanDetails.loan_amount || 0).toFixed(2)}
              {dueDateInfo && dueDateInfo.principal_paid > 0 && (
                <> | Already paid: ₹{dueDateInfo.principal_paid.toFixed(2)} | Remaining: ₹{(parseFloat(loanDetails.loan_amount || 0) - dueDateInfo.principal_paid).toFixed(2)}</>
              )}
            </small>
          </div>
          <div style={{ marginTop: '15px' }}>
            <label>Payment Mode:</label>
            <select
              value={loanPaymentMode}
              onChange={(e) => setLoanPaymentMode(e.target.value)}
              disabled={paymentStatus === 'Paid'}
            >
              <option value="">Select Payment Mode</option>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Cheque">Cheque</option>
            </select>
          </div>
          <div style={{ marginTop: '10px' }}>
            <label>Transaction ID:</label>
            <input
              type="text"
              value={loanTransactionId}
              onChange={(e) => setLoanTransactionId(e.target.value)}
              placeholder="Transaction ID (optional)"
              disabled={paymentStatus === 'Paid'}
            />
          </div>
          <button 
            onClick={handleLoanAmountPayment}
            disabled={paymentStatus === 'Paid'}
            style={{ 
              marginTop: '15px', 
              padding: '10px 20px',
              backgroundColor: paymentStatus === 'Paid' ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: paymentStatus === 'Paid' ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              opacity: paymentStatus === 'Paid' ? 0.6 : 1
            }}
          >
            Pay Loan Amount
          </button>
        </div>
      </div>

      {/* Transaction Table */}
      <div style={{ marginTop: '30px', border: '2px solid #6c757d', padding: '20px', borderRadius: '8px', background: '#f8f9fa' }}>
        <h3 style={{ marginTop: 0, color: '#6c757d' }}>Transaction History</h3>
        
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px' }}>
          <thead>
            <tr style={{ backgroundColor: '#6c757d', color: 'white' }}>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Date</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Type</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Amount</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Payment Mode</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Transaction ID</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Slip</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Combine and sort all transactions by date (newest first)
              const allTransactions = [
                ...interestTransactions.map(t => ({
                  ...t,
                  type: 'Interest',
                  displayAmount: parseFloat(t.interest_amount || t.commission_interest_amount || 0),
                  date: t.payment_date || t.due_date || t.created_at
                })),
                ...partialPaymentTransactions.map(t => ({
                  ...t,
                  type: 'Partial Payment',
                  displayAmount: parseFloat(t.amount || 0),
                  date: t.payment_date || t.due_date || t.created_at
                }))
              ].sort((a, b) => {
                const dateA = new Date(a.date || 0);
                const dateB = new Date(b.date || 0);
                return dateB - dateA; // Sort descending (newest first)
              });

              if (allTransactions.length === 0) {
                return (
                  <tr>
                    <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                      No transactions found
                    </td>
                  </tr>
                );
              }

              return allTransactions.map((transaction, index) => (
                <tr key={`${transaction.type}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                    {formatDateToDDMMYYYY(transaction.date)}
                  </td>
                  <td style={{ 
                    padding: '10px', 
                    border: '1px solid #ccc', 
                    textAlign: 'center', 
                    color: transaction.type === 'Interest' ? '#007bff' : '#28a745', 
                    fontWeight: 'bold' 
                  }}>
                    {transaction.type}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                    ₹{transaction.displayAmount.toFixed(2)}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                    {transaction.payment_mode || 'N/A'}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                    {transaction.transaction_id || 'N/A'}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                    <button
                      onClick={() => {
                        if (transaction.type === 'Interest') {
                          const transactionAmount = transaction.displayAmount;
                          setLastInterestPaid(transactionAmount);
                          generateInterestSlip(transactionAmount);
                        } else {
                          const transactionAmount = transaction.displayAmount;
                          setLastAmountPaid(transactionAmount);
                          const originalPayAmount = payAmount;
                          setPayAmount(transactionAmount.toString());
                          setTimeout(() => {
                            generateTotalAmountSlip();
                            setPayAmount(originalPayAmount);
                          }, 100);
                        }
                      }}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      View Slip
                    </button>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>

      {/* Slip Buttons - Optional manual generation */}
      {isPaymentSuccessful && (
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <button onClick={generateInterestSlip} className="slip-button">Generate Interest Slip</button>
          <button onClick={generateTotalAmountSlip} className="slip-button">Generate Total Amount Slip</button>
        </div>
      )}

      {/* Renewal Confirmation Dialog */}
      {showRenewalDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
              Confirm Interest Payment
            </h2>
            <p style={{ marginBottom: '20px', fontSize: '16px', color: '#555' }}>
              Do you want to renew the loan for another 30 days, or proceed without renewing?
            </p>
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '5px',
              border: '1px solid #dee2e6'
            }}>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                <strong>Interest Amount:</strong> ₹{parseFloat(payInterest || 0).toFixed(2)}
              </p>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                <strong>Payment Date:</strong> {formatDateToDDMMYYYY(paymentDate)}
              </p>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                <strong>Current Loan Amount:</strong> ₹{parseFloat(loanDetails?.loan_amount || 0).toFixed(2)}
              </p>
              {dueDateInfo && dueDateInfo.principal_paid > 0 && (
                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                  <strong>Already Paid:</strong> ₹{dueDateInfo.principal_paid.toFixed(2)} | 
                  <strong> Remaining:</strong> ₹{(parseFloat(loanDetails?.loan_amount || 0) - dueDateInfo.principal_paid).toFixed(2)}
                </p>
              )}
            </div>
            
            {/* Partial Payment Option - Show when user wants to renew */}
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#fff3cd',
              borderRadius: '5px',
              border: '1px solid #ffc107'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showPartialPayment}
                  onChange={(e) => {
                    setShowPartialPayment(e.target.checked);
                    if (!e.target.checked) {
                      setPartialPaymentAmount('');
                    }
                  }}
                  style={{ marginRight: '10px', width: '18px', height: '18px' }}
                />
                <strong style={{ fontSize: '14px' }}>Pay Loan Amount Partially</strong>
              </label>
              {showPartialPayment && (
                <div style={{ marginTop: '10px' }}>
                  <input
                    type="number"
                    value={partialPaymentAmount}
                    onChange={(e) => setPartialPaymentAmount(e.target.value)}
                    placeholder="Enter partial payment amount"
                    step="0.01"
                    min="0"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '5px',
                      border: '1px solid #ccc',
                      fontSize: '14px'
                    }}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                    {dueDateInfo && dueDateInfo.principal_paid > 0 ? (
                      <>Maximum: ₹{(parseFloat(loanDetails?.loan_amount || 0) - dueDateInfo.principal_paid).toFixed(2)}</>
                    ) : (
                      <>Maximum: ₹{parseFloat(loanDetails?.loan_amount || 0).toFixed(2)}</>
                    )}
                  </small>
                  {partialPaymentAmount && parseFloat(partialPaymentAmount) > 0 && (
                    <p style={{ marginTop: '10px', fontSize: '14px', color: '#155724', fontWeight: 'bold' }}>
                      New Loan Amount after deduction: ₹{(
                        parseFloat(loanDetails?.loan_amount || 0) - 
                        (dueDateInfo?.principal_paid || 0) - 
                        parseFloat(partialPaymentAmount || 0)
                      ).toFixed(2)}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowRenewalDialog(false);
                  setShowPartialPayment(false);
                  setPartialPaymentAmount('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPartialPayment(false);
                  setPartialPaymentAmount('');
                  handleInterestPayment(false);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                Pay Without Renewal
              </button>
              <button
                onClick={() => handleInterestPayment(true)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                Renew & Pay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CDLoanPayment;
